import { useCallback, useState } from 'react';

import * as turf from '@turf/turf';

import type { GeoAdminIdentifyFeature } from 'utils/geoAdminIdentifyHelper';
import {
  getParcelFeatureKey,
  getParcelFeaturePolygon,
  mergeSelectedParcelFeatureGroups,
  mergeSelectedParcelFeatures,
} from './cadastralParcelSelection';

interface UseLegalBorderParcelSelectionOptions {
  applyLegalBorderFeature: (feature: GeoJSON.Feature) => boolean;
  applyLegalBorderFeatureGroups?: (features: GeoJSON.Feature[]) => boolean;
  showError: (message: string) => void;
  clearParcelModeUi: () => void;
}

export const useLegalBorderParcelSelection = ({
  applyLegalBorderFeature,
  applyLegalBorderFeatureGroups,
  showError,
  clearParcelModeUi,
}: UseLegalBorderParcelSelectionOptions) => {
  const [selectedLegalBorderParcelFeatures, setSelectedLegalBorderParcelFeatures] = useState<GeoAdminIdentifyFeature[]>(
    []
  );

  const clearSelectedLegalBorderParcelFeatures = useCallback(() => {
    setSelectedLegalBorderParcelFeatures([]);
  }, []);

  const handleLegalBorderParcelSelected = useCallback(
    (feature: GeoJSON.Feature) => {
      const parcelFeature = feature as unknown as GeoAdminIdentifyFeature;
      const nextFeatureKey = getParcelFeatureKey(parcelFeature);

      setSelectedLegalBorderParcelFeatures((currentFeatures) => {
        const isAlreadySelected = currentFeatures.some(
          (currentFeature) => getParcelFeatureKey(currentFeature) === nextFeatureKey
        );
        if (isAlreadySelected) {
          return currentFeatures.filter((currentFeature) => getParcelFeatureKey(currentFeature) !== nextFeatureKey);
        }

        if (!applyLegalBorderFeatureGroups && currentFeatures.length > 0) {
          const candidatePolygon = getParcelFeaturePolygon(parcelFeature).polygon;
          const isAdjacentToCurrentSelection =
            candidatePolygon != null &&
            currentFeatures.some((currentFeature) => {
              const currentPolygon = getParcelFeaturePolygon(currentFeature).polygon;
              if (!currentPolygon) {
                return false;
              }

              try {
                return turf.booleanIntersects(candidatePolygon, currentPolygon);
              } catch {
                return false;
              }
            });

          if (!isAdjacentToCurrentSelection) {
            showError('Only adjacent parcels can be selected together.');
            return currentFeatures;
          }
        }

        return [...currentFeatures, parcelFeature];
      });

      return true;
    },
    [applyLegalBorderFeatureGroups, showError]
  );

  const handleSaveLegalBorderParcelSelection = useCallback(() => {
    if (selectedLegalBorderParcelFeatures.length === 0) {
      clearParcelModeUi();
      return;
    }

    const { features, error } = applyLegalBorderFeatureGroups
      ? mergeSelectedParcelFeatureGroups(selectedLegalBorderParcelFeatures)
      : (() => {
          const result = mergeSelectedParcelFeatures(selectedLegalBorderParcelFeatures);
          return { features: result.feature ? [result.feature] : [], error: result.error };
        })();
    if (features.length === 0) {
      showError(error ?? 'Unable to use one or more cadastral parcels.');
      return;
    }

    const result = applyLegalBorderFeatureGroups?.(features) ?? applyLegalBorderFeature(features[0]);
    if (result === false) {
      return;
    }

    clearSelectedLegalBorderParcelFeatures();
    clearParcelModeUi();
  }, [
    applyLegalBorderFeature,
    applyLegalBorderFeatureGroups,
    clearParcelModeUi,
    clearSelectedLegalBorderParcelFeatures,
    selectedLegalBorderParcelFeatures,
    showError,
  ]);

  return {
    selectedLegalBorderParcelFeatures,
    clearSelectedLegalBorderParcelFeatures,
    handleLegalBorderParcelSelected,
    handleSaveLegalBorderParcelSelection,
  };
};
